import { useState, useEffect, useCallback, useRef } from 'react';
import { BookOpen, Save, Plus, Trash2, X, Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '../lib/api';
import { useToast } from '../hooks/useToast';
import { useAuth } from '../hooks/useAuth';
import { getDateLocale } from '../i18n';

interface KnowledgeEntry {
  id: number;
  language: string;
  section: string;
  content: string;
  updated_at: string;
}

const ALL_SECTIONS = ['services', 'rules', 'pricing', 'faq', 'facilities', 'emergency'];
const ALL_LANGUAGES = ['et', 'en', 'fi', 'de'];

export function KnowledgePage() {
  const { t } = useTranslation();
  const toast = useToast();
  const { activeMarinaId } = useAuth();
  const [entries, setEntries] = useState<KnowledgeEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeLang, setActiveLang] = useState('et');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editContent, setEditContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const [showNewForm, setShowNewForm] = useState(false);
  const [newSection, setNewSection] = useState(ALL_SECTIONS[0]);
  const [newLanguage, setNewLanguage] = useState('et');
  const [newContent, setNewContent] = useState('');
  const [creating, setCreating] = useState(false);

  const toastRef = useRef(toast);
  toastRef.current = toast;

  const fetchKnowledge = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getKnowledge();
      const rows = data.entries as unknown as KnowledgeEntry[];
      setEntries(rows);
      if (rows.length > 0 && !rows.find((r) => r.language === activeLang)) {
        setActiveLang(rows[0].language);
      }
    } catch (err) {
      if (err instanceof ApiError) toastRef.current.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [activeMarinaId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchKnowledge(); }, [fetchKnowledge]);

  const languages = [...new Set(entries.map((e) => e.language))];
  const filtered = entries.filter((e) => e.language === activeLang);

  const usedSections = new Set(entries.filter((e) => e.language === newLanguage).map((e) => e.section));
  const availableSections = ALL_SECTIONS.filter((s) => !usedSections.has(s));

  const startEdit = (entry: KnowledgeEntry) => {
    setEditingId(entry.id);
    setEditContent(entry.content);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditContent('');
  };

  const saveEdit = async (id: number) => {
    if (saving) return;
    setSaving(true);
    try {
      await api.updateKnowledge(id, { content: editContent });
      toast.success(t('common.save'));
      setEditingId(null);
      fetchKnowledge();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('knowledge.failedToSave'));
    } finally {
      setSaving(false);
    }
  };

  const deleteEntry = async (id: number) => {
    if (!confirm(t('knowledge.deleteConfirm'))) return;
    setDeletingId(id);
    try {
      await api.deleteKnowledge(id);
      toast.success(t('knowledge.entryDeleted'));
      fetchKnowledge();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('knowledge.failedToDelete'));
    } finally {
      setDeletingId(null);
    }
  };

  const createEntry = async () => {
    if (!newContent.trim()) {
      toast.error(t('knowledge.contentEmpty'));
      return;
    }
    setCreating(true);
    try {
      await api.createKnowledge({ section: newSection, language: newLanguage, content: newContent.trim() });
      toast.success(t('knowledge.entryCreated'));
      setShowNewForm(false);
      setNewContent('');
      setNewSection(ALL_SECTIONS[0]);
      setActiveLang(newLanguage);
      fetchKnowledge();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('knowledge.failedToCreate'));
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-4 border-brand-200 border-t-brand-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">{t('knowledge.title')}</h1>
          <p className="text-sm text-gray-500 mt-1">{t('knowledge.subtitle')}</p>
        </div>
        <button
          onClick={() => { setShowNewForm(true); setNewSection(availableSections[0] ?? ALL_SECTIONS[0]); }}
          className="inline-flex items-center gap-1.5 px-3 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          {t('knowledge.newEntry')}
        </button>
      </div>

      {/* New entry form */}
      {showNewForm && (
        <div className="bg-white rounded-xl border border-brand-200 shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100 bg-brand-50/50 flex items-center justify-between">
            <span className="text-sm font-semibold text-gray-800 flex items-center gap-2">
              <Plus className="w-4 h-4 text-brand-600" /> {t('knowledge.newEntryTitle')}
            </span>
            <button onClick={() => setShowNewForm(false)} className="text-gray-400 hover:text-gray-600">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="p-5 space-y-4">
            <div className="flex gap-3">
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-700 mb-1">{t('knowledge.language')}</label>
                <select
                  value={newLanguage}
                  onChange={(e) => {
                    setNewLanguage(e.target.value);
                    const used = new Set(entries.filter((en) => en.language === e.target.value).map((en) => en.section));
                    const avail = ALL_SECTIONS.filter((s) => !used.has(s));
                    setNewSection(avail[0] ?? ALL_SECTIONS[0]);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/40"
                >
                  {ALL_LANGUAGES.map((l) => (
                    <option key={l} value={l}>{t(`knowledge.languages.${l}` as Parameters<typeof t>[0])}</option>
                  ))}
                </select>
              </div>
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-700 mb-1">{t('knowledge.section')}</label>
                <select
                  value={newSection}
                  onChange={(e) => setNewSection(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/40"
                >
                  {ALL_SECTIONS.map((s) => {
                    const used = entries.some((e) => e.language === newLanguage && e.section === s);
                    return (
                      <option key={s} value={s} disabled={used}>
                        {t(`knowledge.sections.${s}` as Parameters<typeof t>[0])}{used ? ` ${t('knowledge.exists')}` : ''}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('knowledge.content')}</label>
              <textarea
                value={newContent}
                onChange={(e) => setNewContent(e.target.value)}
                rows={8}
                placeholder={t('knowledge.contentPlaceholder')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-500/40 resize-y"
                autoFocus
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowNewForm(false)}
                className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={createEntry}
                disabled={creating}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 disabled:opacity-50 transition-colors"
              >
                <Save className="w-3.5 h-3.5" />
                {creating ? t('common.creating') : t('knowledge.createEntry')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Language tabs */}
      {languages.length > 0 && (
        <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit">
          {languages.map((lang) => (
            <button
              key={lang}
              onClick={() => { setActiveLang(lang); cancelEdit(); }}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                activeLang === lang ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Globe className="w-3.5 h-3.5" />
              {t(`knowledge.languages.${lang}` as Parameters<typeof t>[0], { defaultValue: lang.toUpperCase() })}
            </button>
          ))}
        </div>
      )}

      {entries.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 px-5 py-16 text-center">
          <BookOpen className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-sm font-medium text-gray-600 mb-1">{t('knowledge.noEntries')}</p>
          <p className="text-xs text-gray-400 mb-4">{t('knowledge.noEntriesSubtitle')}</p>
          <button
            onClick={() => setShowNewForm(true)}
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 transition-colors"
          >
            <Plus className="w-4 h-4" /> {t('knowledge.addFirstEntry')}
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 px-5 py-12 text-center text-sm text-gray-400">
          {t('knowledge.noEntriesForLang', { lang: t(`knowledge.languages.${activeLang}` as Parameters<typeof t>[0], { defaultValue: activeLang }) })}
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map((entry) => {
            const isEditing = editingId === entry.id;
            return (
              <div key={entry.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
                  <div className="flex items-center gap-2">
                    <BookOpen className="w-4 h-4 text-brand-600" />
                    <span className="text-sm font-semibold text-gray-800">
                      {t(`knowledge.sections.${entry.section}` as Parameters<typeof t>[0], { defaultValue: entry.section })}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    {!isEditing ? (
                      <>
                        <button
                          onClick={() => startEdit(entry)}
                          className="text-xs text-brand-600 hover:text-brand-700 font-medium"
                        >
                          {t('common.edit')}
                        </button>
                        <button
                          onClick={() => deleteEntry(entry.id)}
                          disabled={deletingId === entry.id}
                          className="text-xs text-red-500 hover:text-red-700 disabled:opacity-40"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </>
                    ) : (
                      <div className="flex items-center gap-2">
                        <button onClick={cancelEdit} className="text-xs text-gray-500 hover:text-gray-700">
                          {t('common.cancel')}
                        </button>
                        <button
                          onClick={() => saveEdit(entry.id)}
                          disabled={saving}
                          className="inline-flex items-center gap-1 text-xs text-brand-600 hover:text-brand-700 font-medium disabled:opacity-50"
                        >
                          <Save className="w-3 h-3" /> {saving ? t('common.saving') : t('common.save')}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="p-5">
                  {isEditing ? (
                    <textarea
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      rows={10}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-500/40 resize-y"
                      autoFocus
                    />
                  ) : (
                    <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans leading-relaxed">
                      {entry.content || <span className="text-gray-400 italic">{t('common.edit')}</span>}
                    </pre>
                  )}
                  <div className="mt-3 text-xs text-gray-400">
                    {t('knowledge.lastUpdated', {
                      date: new Date(entry.updated_at).toLocaleDateString(getDateLocale(), { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }),
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
